import { Box, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useViewportDragHandle } from '~/viewportGrid/adapters/engine/overlays/useViewportDragHandle';
import { MOUSE_BUTTONS_NONE } from '~/engine/tool/MouseButton';
import { CrosshairInteractionMode } from '~/engine/tool/ToolManager';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { useCrosshairInteraction } from '~/hooks/useCrosshairInteraction';

/** One crosshair line: `horizontal` moves up and down, `vertical` sideways. */
export type CrosshairAxis = 'horizontal' | 'vertical';

/** A grabbable part of the crosshair: one line, or the centre handle. */
type CrosshairPart = CrosshairAxis | 'center';

const CROSSHAIR_PARTS: ReadonlyArray<CrosshairPart> = [
    'horizontal',
    'vertical',
    'center',
];

const PART_CURSOR: Record<CrosshairPart, string> = {
    horizontal: 'ns-resize',
    vertical: 'ew-resize',
    center: 'move',
};

interface CrosshairsProps {
    view: PlaneView | null;
    vertical: number;
    horizontal: number;
    handleCrosshairChange: (type: CrosshairAxis, delta: number) => void;
}

const Crosshairs = ({
    view,
    horizontal,
    vertical,
    handleCrosshairChange,
}: CrosshairsProps) => {
    const theme = useTheme();
    const svgRef = useRef<SVGSVGElement>(null);

    const { isDragging, isDraggingNow, startDrag } = useViewportDragHandle();

    const interactionMode = useCrosshairInteraction(view);

    // Whether the line bodies and centre handle accept mouse events.
    const isLineEnabled =
        !isDragging && interactionMode === CrosshairInteractionMode.Full;
    // Whether the root handles at the line endpoints accept mouse events.
    const isRootHandleEnabled =
        !isDragging &&
        (interactionMode === CrosshairInteractionMode.Full ||
            interactionMode === CrosshairInteractionMode.RootsOnly);
    // Whether to render lines with a visual indicator that they are
    // disabled. True whenever a drawing tool is active (both between
    // strokes and during a stroke) so the indicator does not flash on
    // and off during mouse-button-held periods.
    const showDisabledVisual =
        interactionMode === CrosshairInteractionMode.RootsOnly ||
        interactionMode === CrosshairInteractionMode.DrawingStroke;

    const [hoveredLine, setHoveredLine] = useState<CrosshairPart | null>(null);
    // The part the current drag holds. A drag disables hover, so the
    // highlight follows this while the drag lasts.
    const [grabbedLine, setGrabbedLine] = useState<CrosshairPart | null>(null);
    const activeLine = isDragging ? grabbedLine : hoveredLine;
    // Where the last drag ended, until the lines accept mouse events again.
    const releasePointRef = useRef<{ x: number; y: number } | null>(null);

    // Clear visual hover state when line interaction becomes disallowed —
    // no onMouseLeave will fire because pointer-events switches to 'none'.
    useEffect(() => {
        if (!isLineEnabled) {
            setHoveredLine(null);
        }
    }, [isLineEnabled]);

    // After a drag the hover is the part under the release point, found
    // once the lines accept mouse events again. The pointer that released
    // a line usually rests on it, and no onMouseEnter fires until it moves.
    useEffect(() => {
        const point = releasePointRef.current;
        if (isDragging || point === null) {
            return;
        }
        releasePointRef.current = null;
        const element = document.elementFromPoint(point.x, point.y);
        const part =
            element && svgRef.current?.contains(element)
                ? element.getAttribute('data-crosshair-part')
                : null;
        setHoveredLine(CROSSHAIR_PARTS.find((p) => p === part) ?? null);
    }, [isDragging]);

    const isHorizontalDraggerWithinBoundaries =
        horizontal !== undefined && horizontal >= 0 && horizontal <= 100;
    const isVerticalDraggerWithinBoundaries =
        vertical !== undefined && vertical >= 0 && vertical <= 100;

    const handleMouseDown = (
        event: React.MouseEvent<HTMLDivElement | SVGElement, MouseEvent>,
        type: CrosshairPart,
        isRootHandle = false
    ) => {
        event.preventDefault();

        const allowed = isRootHandle ? isRootHandleEnabled : isLineEnabled;
        if (!allowed) {
            return;
        }

        setGrabbedLine(type);

        const dragCursor = PART_CURSOR[type];

        const startPos = { x: event.clientX, y: event.clientY };

        const onMouseMove = (moveEvent: MouseEvent) => {
            const { clientX, clientY } = moveEvent;
            let delta: number;

            if (type === 'center') {
                // Handle both axes simultaneously
                const deltaX = clientX - startPos.x;
                const deltaY = clientY - startPos.y;

                startPos.x = clientX;
                startPos.y = clientY;

                handleCrosshairChange('vertical', deltaX);
                handleCrosshairChange('horizontal', deltaY);
                return;
            }

            if (type === 'horizontal') {
                delta = clientY - startPos.y;
                startPos.y = clientY;
            } else {
                delta = clientX - startPos.x;
                startPos.x = clientX;
            }
            handleCrosshairChange(type, delta);
        };

        startDrag({
            cursor: dragCursor,
            onMove: onMouseMove,
            onEnd: ({ clientX, clientY }) => {
                releasePointRef.current = { x: clientX, y: clientY };
            },
        });
    };

    // A line is active while hovered or dragged, on its own or through the
    // centre handle.
    const isActive = (axis: CrosshairAxis): boolean =>
        activeLine === axis || activeLine === 'center';

    const lineProps = (axis: CrosshairAxis) => ({
        axis,
        isLineEnabled,
        isRootHandleEnabled,
        isActive: isActive(axis),
        showDisabledVisual,
        isDraggingNow,
        onGrab: (event: React.MouseEvent<SVGElement>, isRootHandle: boolean) =>
            handleMouseDown(event, axis, isRootHandle),
        onHover: (isHovered: boolean) =>
            setHoveredLine(isHovered ? axis : null),
    });

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 6,
            }}
        >
            {/* SVG for lines */}
            <svg
                ref={svgRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                }}
            >
                {isHorizontalDraggerWithinBoundaries && (
                    <CrosshairLine
                        position={horizontal}
                        {...lineProps('horizontal')}
                    />
                )}

                {isVerticalDraggerWithinBoundaries && (
                    <CrosshairLine
                        position={vertical}
                        {...lineProps('vertical')}
                    />
                )}

                {/* Centre intersection handle for dragging both axes */}
                {isHorizontalDraggerWithinBoundaries &&
                    isVerticalDraggerWithinBoundaries && (
                        <circle
                            data-crosshair-part="center"
                            cx={`${vertical}%`}
                            cy={`${horizontal}%`}
                            r={6}
                            fill={theme.palette.primary.main}
                            opacity={0}
                            stroke={theme.palette.primary.dark}
                            strokeWidth="1"
                            style={{
                                pointerEvents: isLineEnabled ? 'auto' : 'none',
                                cursor:
                                    activeLine === 'center'
                                        ? PART_CURSOR.center
                                        : undefined,
                            }}
                            onMouseDown={(event) =>
                                handleMouseDown(event, 'center')
                            }
                            onMouseEnter={(event) => {
                                if (event.buttons === MOUSE_BUTTONS_NONE) {
                                    setHoveredLine('center');
                                }
                            }}
                            onMouseLeave={() => {
                                if (!isDraggingNow()) {
                                    setHoveredLine(null);
                                }
                            }}
                        />
                    )}
            </svg>
        </Box>
    );
};

/** The two ends of a line across the whole tile at `position` percent. */
function lineEnds(axis: CrosshairAxis, position: number) {
    const at = `${position}%`;
    return axis === 'horizontal'
        ? { x1: '0%', y1: at, x2: '100%', y2: at }
        : { x1: at, y1: '0%', x2: at, y2: '100%' };
}

interface CrosshairLineProps {
    readonly axis: CrosshairAxis;
    /** Where the line crosses the tile, in percent of the tile. */
    readonly position: number;
    readonly isLineEnabled: boolean;
    readonly isRootHandleEnabled: boolean;
    readonly isActive: boolean;
    readonly showDisabledVisual: boolean;
    readonly isDraggingNow: () => boolean;
    readonly onGrab: (
        event: React.MouseEvent<SVGElement>,
        isRootHandle: boolean
    ) => void;
    readonly onHover: (isHovered: boolean) => void;
}

/**
 * One crosshair line: an invisible hit area along its length, the visible
 * line, and a root handle at each end. An active line draws in the primary
 * colour over a soft halo. Line colour and opacity change when the drawing
 * tool is active, to signal to the user that full-line dragging is not
 * available.
 */
function CrosshairLine({
    axis,
    position,
    isLineEnabled,
    isRootHandleEnabled,
    isActive,
    showDisabledVisual,
    isDraggingNow,
    onGrab,
    onHover,
}: CrosshairLineProps) {
    const theme = useTheme();
    const ends = lineEnds(axis, position);

    const lineStyle = isActive
        ? { stroke: theme.palette.primary.main, strokeWidth: 2, opacity: 1 }
        : {
              stroke: showDisabledVisual
                  ? theme.palette.grey[600]
                  : theme.palette.grey[500],
              strokeWidth: 1.5,
              opacity: showDisabledVisual ? 0.35 : 0.8,
          };

    // Root handles appear as small circles at the line endpoints.
    // They become visible when a drawing tool is active so the user can
    // see they are still interactive.
    const rootHandleOpacity = showDisabledVisual ? 0.75 : 0;
    const rootHandleRadius = 9;
    // Extra invisible pixels around each root handle to make it easier
    // to grab — a slightly larger hit zone without visual change.
    const rootHandleHitRadius = rootHandleRadius + 4;
    const rootHandleColor = theme.palette.primary.light;

    return (
        <>
            {/* Invisible hit area for the full line */}
            <line
                data-testid={`crosshair-hit-${axis}`}
                data-crosshair-part={axis}
                {...ends}
                stroke="transparent"
                strokeWidth="10"
                style={{
                    pointerEvents: isLineEnabled ? 'auto' : 'none',
                    cursor: isActive ? PART_CURSOR[axis] : undefined,
                }}
                onMouseDown={(event) => onGrab(event, false)}
                onMouseEnter={(event) => {
                    if (event.buttons === MOUSE_BUTTONS_NONE) {
                        onHover(true);
                    }
                }}
                onMouseLeave={() => {
                    if (!isDraggingNow()) {
                        onHover(false);
                    }
                }}
            />
            {isActive && (
                <line
                    data-testid={`crosshair-halo-${axis}`}
                    {...ends}
                    stroke={theme.palette.primary.main}
                    strokeWidth={6}
                    opacity={0.25}
                    style={{ pointerEvents: 'none' }}
                />
            )}
            {/* Visible line */}
            <line
                data-testid={`crosshair-line-${axis}`}
                {...ends}
                {...lineStyle}
                style={{ pointerEvents: 'none' }}
            />
            {[
                { key: 'start', cx: ends.x1, cy: ends.y1 },
                { key: 'end', cx: ends.x2, cy: ends.y2 },
            ].map(({ key, ...end }) => (
                <React.Fragment key={key}>
                    {/* Invisible expanded hit area */}
                    <circle
                        {...end}
                        r={rootHandleHitRadius}
                        fill="transparent"
                        style={{
                            pointerEvents: isRootHandleEnabled
                                ? 'auto'
                                : 'none',
                            cursor: PART_CURSOR[axis],
                        }}
                        onMouseDown={(event) => onGrab(event, true)}
                    />
                    {/* Visible handle */}
                    <circle
                        {...end}
                        r={rootHandleRadius}
                        fill={rootHandleColor}
                        opacity={rootHandleOpacity}
                        style={{ pointerEvents: 'none' }}
                    />
                </React.Fragment>
            ))}
        </>
    );
}

export default Crosshairs;
